import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {
  AppShell,
  FloatingPanel,
  NotificationFab,
  NotificationPanelContent,
  NotificationPanelFooter,
  NotificationPanelHeader,
} from '@pulse/ui';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { navigation, APP_VERSION } from '../config/navigation';
import { filterNavByPermissions } from '../config/navigation.utils';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { useLogout } from '../features/auth/hooks/useLogout';

// Reads NotificationContext and hands it to the presentational FloatingPanel/Notification*
// components as plain props — keeps those (packages/ui) unaware of where their data comes
// from, the same boundary AppShell/AppLayout already draw for nav/user state. FloatingPanel
// itself is content-agnostic (see packages/ui/CLAUDE.md); this is the one place that slots
// notification-specific pieces into it.
function GlobalNotificationPanel() {
  const { notifications, unreadCount, markRead, dismiss } = useNotifications();
  const navigate = useNavigate();

  // The panel is an "needs my attention" queue, not a history — reading a notification (here,
  // or by opening it on the Notifications page, which syncs back via syncRead) takes it out of
  // the list. Filtered at render rather than in NotificationContext's reducer so the context
  // keeps holding read items: MARK_READ/SYNC_READ/MARK_UNREAD all look the item up in
  // state.notifications first, and dropping it would break that bookkeeping (and the panel's
  // own mark-as-read action would have nothing to toggle). The full history stays on
  // /notifications, one click away via the footer's "View all".
  const unread = notifications.filter((n) => !n.read);

  return (
    <FloatingPanel
      title="Notifications"
      fab={<NotificationFab unreadCount={unreadCount} />}
      header={<NotificationPanelHeader unreadCount={unreadCount} />}
      content={
        <NotificationPanelContent
          notifications={unread}
          onMarkRead={markRead}
          onDismiss={dismiss}
          maxHeight="50vh"
        />
      }
      footer={<NotificationPanelFooter onViewAll={() => navigate('/notifications')} />}
    />
  );
}

// Wraps every protected page (see App.tsx's layout route) with the side-nav shell. Threads
// react-router state into the router-agnostic AppShell from @pulse/ui as plain props, so
// that package never needs to import react-router-dom itself. NotificationProvider sits
// around this component in App.tsx rather than inside it, so the unread badge below can read
// the context here.
export function AppLayout() {
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  // Echoes the access token's permissions claim (set server-side in MeResponseDto) rather
  // than re-deriving from user.roles here — see packages/shared's IUser for why.
  const permissions = user?.permissions ?? [];
  const roleLabel = user?.roles[0]?.name ?? 'No role';
  // Approvals is gated by approval-group *membership* (an active `group_members` row, surfaced
  // as IUser.isApprover), not by a permission — so it can't ride on filterNavByPermissions
  // below, whose only vocabulary is Permission. Non-approvers would otherwise see the entry and
  // open it onto a permanently empty list. Dropping the whole domain is exact today: it holds
  // only Funding Exemption Approval — revisit if a second page is ever added under it.
  const navForUser = user?.isApprover ? navigation : navigation.filter((node) => node.id !== 'approvals');
  const visibleNav = filterNavByPermissions(navForUser, permissions);

  return (
    <>
      <AppShell
        nav={visibleNav}
        // Pinned above the user section rather than listed in `navigation.tsx` — it's the
        // rail's counterpart to the notification panel's own "View all" footer link, so it
        // stays reachable without scrolling the nav tree. No permission gate: the inbox API
        // is guarded by JwtAuthGuard alone, so every signed-in user has one.
        footerNav={{
          kind: 'page',
          id: 'view-all-notifications',
          label: 'All notifications',
          icon: <NotificationsNoneIcon fontSize="small" />,
          path: '/notifications',
          badgeCount: unreadCount,
        }}
        activePath={location.pathname}
        onNavigate={(path) => navigate(path)}
        productIcon={<img src="/logo.svg" alt="Pulse 2.0" width={28} height={28} />}
        productName="Pulse 2.0"
        productVersion={APP_VERSION}
        userName={user?.name ?? 'there'}
        userEmail={user?.email ?? ''}
        userAvatarUrl={user?.avatarUrl}
        userRoleLabel={roleLabel}
        userActions={[
          {
            label: 'Sign out',
            icon: <LogoutIcon fontSize="small" />,
            onClick: () => logoutMutation.mutate(),
          },
        ]}
      >
        <Outlet />
      </AppShell>
      <GlobalNotificationPanel />
    </>
  );
}
